import { useCamaraFotos } from '@/components/fotos/CamaraFotos';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  useAnularRegistro,
  useCerrarRegistro,
  useEliminarFoto,
  useGuardarRespuestas,
  useIniciarRegistro,
  useLiberarRegistro,
  useRegistro,
} from '@/hooks/useCalidad';
import { esAdmin, esGestion, esSoloLectura } from '@/lib/cliente';
import { EVENTO_FOTOS_SUBIDAS, listarPendientes, suscribir } from '@/lib/colaFotos';
import { cn } from '@/lib/utils';
import type {
  FotoCalidad,
  GuardarFicha,
  PuntoControl,
  RegistroDetalle,
  ResultadoPunto,
  ResultadoRegistro,
} from '@adelante/shared';
import {
  Camera,
  CaretDown,
  CaretLeft,
  CheckCircle,
  CircleNotch,
  Prohibit,
  SealCheck,
  Trash,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Llenado de una ficha de control (inspección o evaluación) en campo.
 *
 * Cada respuesta se guarda al tocarla (valor y nota un segundo después de
 * dejar de escribir), así un registro puede completarse por etapas o por varias
 * personas. Lo que no llega al servidor (sin señal, app cerrada a media
 * escritura) queda respaldado en el teléfono y se reenvía al volver. Al cerrar,
 * el backend calcula el resultado:
 *   obligatorio no cumple → rechazado (inspección: bloquea el inicio)
 *   algún no cumple       → aprobado con observaciones (genera NC)
 */

interface RespLocal {
  resultado: ResultadoPunto | null;
  valor: string;
  calificacion: number | null;
  nota: string;
}

const RESULTADO: Record<ResultadoRegistro, { label: string; clase: string }> = {
  aprobado: {
    label: 'Aprobado',
    clase: 'bg-adelante-primary/15 text-adelante-primary-dark border-adelante-primary',
  },
  aprobado_obs: {
    label: 'Aprobado con observaciones',
    clase: 'bg-amber-50 text-amber-800 border-amber-300',
  },
  rechazado: { label: 'Rechazado', clase: 'bg-red-50 text-red-700 border-red-300' },
};

function fueraDeRango(p: PuntoControl, valor: string): boolean {
  if (p.tipo_valor !== 'valor' || valor.trim() === '') return false;
  const n = Number(valor);
  if (Number.isNaN(n)) return true;
  return (p.valor_min !== null && n < p.valor_min) || (p.valor_max !== null && n > p.valor_max);
}

const fechaHora = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('es-CR', { dateStyle: 'medium', timeStyle: 'short' }) : '';

/** "Inspección N° 1" / "Reinspección N° 1 (intento 2)" / "Evaluación". */
export function etiquetaIntento(tipo: 'inspeccion' | 'evaluacion', intento: number): string {
  if (tipo === 'evaluacion') return intento > 1 ? `Evaluación (intento ${intento})` : 'Evaluación';
  return intento > 1 ? `Reinspección N° ${intento - 1} (intento ${intento})` : 'Inspección N° 1';
}

/** Sección contraíble con su avance (respondidos / total) y fallas. */
function SeccionColapsable({
  nombre,
  codigo,
  total,
  respondidos,
  fallas,
  children,
}: {
  nombre: string;
  codigo?: string | null;
  total: number;
  respondidos: number;
  fallas: number;
  children: React.ReactNode;
}) {
  const [abierta, setAbierta] = useState(true);
  const completa = total > 0 && respondidos === total;
  return (
    <section className="space-y-2">
      <button
        type="button"
        onClick={() => setAbierta((a) => !a)}
        className="flex w-full items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-left"
        aria-expanded={abierta}
      >
        <CaretDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            !abierta && '-rotate-90',
          )}
          weight="bold"
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
          {nombre || '(sección sin nombre)'}
          {codigo && (
            <span className="ml-1.5 font-mono text-[11px] font-normal text-muted-foreground">
              {codigo}
            </span>
          )}
        </span>
        {fallas > 0 && (
          <span className="shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
            {fallas} no cumple
          </span>
        )}
        <span
          className={cn(
            'flex shrink-0 items-center gap-0.5 text-[11px] tabular-nums',
            completa ? 'font-semibold text-adelante-primary-dark' : 'text-muted-foreground',
          )}
        >
          {completa && <CheckCircle className="h-3.5 w-3.5" weight="fill" aria-hidden="true" />}
          {respondidos}/{total}
        </span>
      </button>
      {abierta && <div className="space-y-2">{children}</div>}
    </section>
  );
}

export function RegistroCalidadPantalla() {
  const { id = '' } = useParams<{ id: string }>();
  const q = useRegistro(id);

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
        Cargando ficha…
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Card className="flex flex-col items-center gap-2 p-6 text-center">
        <WarningCircle className="h-8 w-8 text-red-500" weight="fill" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          {q.error instanceof Error ? q.error.message : 'No se pudo cargar la ficha.'}
        </p>
        <Button variant="outline" size="sm" onClick={() => q.refetch()}>
          Reintentar
        </Button>
      </Card>
    );
  }
  // key: reinicia el estado local al cambiar de registro
  return <RegistroVista key={q.data.id} reg={q.data} />;
}

/**
 * Vista previa de una ficha tal como la ve el usuario en campo (admin). Usa
 * los mismos componentes que la pantalla real; las respuestas solo viven en
 * memoria y no se sube ninguna foto.
 */
export function VistaPreviaFicha({ ficha }: { ficha: GuardarFicha }) {
  const [resp, setResp] = useState<Record<string, RespLocal>>({});
  // Ids temporales estables para los puntos aún no guardados.
  const secciones = ficha.secciones.map((s, i) => ({
    nombre: s.nombre,
    puntos: s.puntos.map(
      (p, j): PuntoControl => ({
        id: p.id ?? `prev-${i}-${j}`,
        texto: p.texto || '(sin texto)',
        criterio: p.criterio ?? null,
        orden: j + 1,
        obligatorio: p.obligatorio,
        requiere_foto: p.requiere_foto,
        tipo_valor: p.tipo_valor,
        unidad: p.unidad ?? null,
        valor_min: p.valor_min ?? null,
        valor_max: p.valor_max ?? null,
        activo: true,
      }),
    ),
  }));
  const puntos = secciones.flatMap((s) => s.puntos);
  const respondidos = puntos.filter((p) => resp[p.id]?.resultado).length;
  const fallas = puntos.filter((p) => resp[p.id]?.resultado === 'no_cumple');
  const previsto: ResultadoRegistro = fallas.some((p) => p.obligatorio)
    ? 'rechazado'
    : fallas.length > 0
      ? 'aprobado_obs'
      : 'aprobado';

  return (
    <div className="space-y-4">
      <Card className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-base font-semibold">{ficha.nombre || 'Ficha sin nombre'}</p>
            <p className="text-xs text-muted-foreground">
              <span className="font-mono">VN-X.00</span> · obra de ejemplo
            </p>
          </div>
          <Badge variant="outline">{etiquetaIntento(ficha.tipo, 1)}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Inspector: <span className="font-medium text-foreground">(usuario que la realiza)</span> ·
          fecha y hora
        </p>
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Respondidos</span>
          <span className="font-semibold tabular-nums">
            {respondidos}/{puntos.length}
          </span>
        </div>
        {respondidos > 0 && (
          <p
            className={cn(
              'rounded-md border px-2 py-1 text-xs font-semibold',
              RESULTADO[previsto].clase,
            )}
          >
            Resultado si se cerrara ahora: {RESULTADO[previsto].label}
          </p>
        )}
      </Card>
      {secciones.map((sec, i) => (
        <SeccionColapsable
          key={`${sec.nombre}-${i}`}
          nombre={sec.nombre}
          total={sec.puntos.length}
          respondidos={sec.puntos.filter((p) => resp[p.id]?.resultado).length}
          fallas={sec.puntos.filter((p) => resp[p.id]?.resultado === 'no_cumple').length}
        >
          {sec.puntos.map((p) => (
            <PuntoTarjeta
              key={p.id}
              punto={p}
              resp={resp[p.id]}
              fotos={[]}
              registroId=""
              editable
              vistaPrevia
              onCambio={(c) =>
                setResp((prev) => ({
                  ...prev,
                  [p.id]: {
                    ...(prev[p.id] ?? { resultado: null, valor: '', calificacion: null, nota: '' }),
                    ...c,
                  },
                }))
              }
            />
          ))}
        </SeccionColapsable>
      ))}
    </div>
  );
}

// Respuestas que todavía no llegan al servidor, por registro (localStorage).
const claveRespaldo = (id: string) => `obc-inspeccion-${id.toUpperCase()}`;

function leerRespaldo(id: string): Record<string, RespLocal> {
  try {
    return JSON.parse(localStorage.getItem(claveRespaldo(id)) ?? '{}');
  } catch {
    return {};
  }
}

function escribirRespaldo(id: string, cambio: (m: Record<string, RespLocal>) => void) {
  try {
    const m = leerRespaldo(id);
    cambio(m);
    if (Object.keys(m).length === 0) localStorage.removeItem(claveRespaldo(id));
    else localStorage.setItem(claveRespaldo(id), JSON.stringify(m));
  } catch {
    /* sin almacenamiento: solo se pierde el respaldo */
  }
}

/** Fotos en la cola del teléfono para este registro, por punto. */
function useFotosEnCola(registroId: string) {
  const qc = useQueryClient();
  const [porPunto, setPorPunto] = useState<Map<string, number>>(new Map());
  useEffect(
    () =>
      suscribir(() => {
        void listarPendientes().then((fs) => {
          const m = new Map<string, number>();
          for (const f of fs) {
            if (f.vinculo.registro_id?.toUpperCase() !== registroId.toUpperCase()) continue;
            const k = (f.vinculo.punto_id ?? '').toUpperCase();
            m.set(k, (m.get(k) ?? 0) + 1);
          }
          setPorPunto(m);
        });
      }),
    [registroId],
  );
  useEffect(() => {
    const refrescar = () => qc.invalidateQueries({ queryKey: ['calidad', 'registro', registroId] });
    window.addEventListener(EVENTO_FOTOS_SUBIDAS, refrescar);
    return () => window.removeEventListener(EVENTO_FOTOS_SUBIDAS, refrescar);
  }, [qc, registroId]);
  return porPunto;
}

function RegistroVista({ reg }: { reg: RegistroDetalle }) {
  const navigate = useNavigate();
  const editable = reg.estado === 'borrador' && !esSoloLectura();
  const guardar = useGuardarRespuestas(reg.id);
  const cerrar = useCerrarRegistro(reg.id);
  const iniciar = useIniciarRegistro();
  const fotosEnCola = useFotosEnCola(reg.id);

  // Estado local de respuestas (fuente de verdad mientras se llena). Encima
  // de lo del servidor va lo respaldado en el teléfono que no se llegó a enviar.
  const [resp, setResp] = useState<Record<string, RespLocal>>(() => {
    const m: Record<string, RespLocal> = {};
    for (const r of reg.respuestas) {
      m[r.punto_id.toUpperCase()] = {
        resultado: r.resultado,
        valor: r.valor === null ? '' : String(r.valor),
        calificacion: r.calificacion,
        nota: r.nota ?? '',
      };
    }
    return reg.estado === 'borrador' ? { ...m, ...leerRespaldo(reg.id) } : m;
  });
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  const [notaCierre, setNotaCierre] = useState('');

  const puntos = useMemo(() => reg.ficha.secciones.flatMap((s) => s.puntos), [reg.ficha]);
  // Quién respondió cada punto (se muestra si no es quien inició la ficha).
  const respondidoPor = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const r of reg.respuestas) m.set(r.punto_id.toUpperCase(), r.respondido_por_nombre);
    return m;
  }, [reg.respuestas]);
  const respondidos = puntos.filter((p) => resp[p.id.toUpperCase()]?.resultado).length;

  const fotosPorPunto = useMemo(() => {
    const m = new Map<string, FotoCalidad[]>();
    for (const f of reg.fotos) {
      const k = (f.punto_id ?? '').toUpperCase();
      m.set(k, [...(m.get(k) ?? []), f]);
    }
    return m;
  }, [reg.fotos]);

  // Resultado previsto (misma regla que el backend) para el diálogo de cierre.
  const previsto: ResultadoRegistro = useMemo(() => {
    const fallas = puntos.filter((p) => resp[p.id.toUpperCase()]?.resultado === 'no_cumple');
    if (fallas.some((p) => p.obligatorio)) return 'rechazado';
    return fallas.length > 0 ? 'aprobado_obs' : 'aprobado';
  }, [puntos, resp]);

  const persistir = useCallback(
    async (puntoId: string, r: RespLocal) => {
      if (!r.resultado) return;
      const valor = r.valor.trim() === '' ? null : Number(r.valor);
      try {
        await guardar.mutateAsync({
          respuestas: [
            {
              punto_id: puntoId,
              resultado: r.resultado,
              valor: valor !== null && !Number.isNaN(valor) ? valor : null,
              calificacion: r.calificacion,
              nota: r.nota.trim() || null,
            },
          ],
        });
        // Solo se borra del respaldo si no cambió mientras se enviaba.
        escribirRespaldo(reg.id, (m) => {
          const k = puntoId.toUpperCase();
          if (JSON.stringify(m[k]) === JSON.stringify(r)) delete m[k];
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Error desconocido';
        if (/HTTP \d{3}/.test(msg))
          toast.error('No se guardó la respuesta', { id: 'resp-error', description: msg });
        else
          toast.warning('Sin conexión', {
            id: 'resp-offline',
            description: 'Las respuestas quedan en el teléfono y se envían al volver la señal.',
          });
      }
    },
    [guardar.mutateAsync, reg.id],
  );

  // Valor y nota: se guardan 1 s después de dejar de escribir (o al salir de la app).
  const pendientes = useRef(new Map<string, { t: ReturnType<typeof setTimeout>; r: RespLocal }>());
  const vaciarPendientes = useCallback(() => {
    for (const [id, { t, r }] of pendientes.current) {
      clearTimeout(t);
      void persistir(id, r);
    }
    pendientes.current.clear();
  }, [persistir]);

  useEffect(() => {
    if (reg.estado !== 'borrador') return;
    const alOcultar = () => document.visibilityState === 'hidden' && vaciarPendientes();
    // Reenvía lo respaldado al abrir y al volver la conexión.
    const reenviar = () => {
      for (const [id, r] of Object.entries(leerRespaldo(reg.id))) void persistir(id, r);
    };
    reenviar();
    document.addEventListener('visibilitychange', alOcultar);
    window.addEventListener('pagehide', vaciarPendientes);
    window.addEventListener('online', reenviar);
    return () => {
      document.removeEventListener('visibilitychange', alOcultar);
      window.removeEventListener('pagehide', vaciarPendientes);
      window.removeEventListener('online', reenviar);
      vaciarPendientes();
    };
  }, [reg.id, reg.estado, persistir, vaciarPendientes]);

  function actualizar(p: PuntoControl, cambio: Partial<RespLocal>, guardarYa: boolean) {
    const k = p.id.toUpperCase();
    const actual = resp[k] ?? { resultado: null, valor: '', calificacion: null, nota: '' };
    const nuevo = { ...actual, ...cambio };
    setResp((prev) => ({ ...prev, [k]: nuevo }));
    if (!nuevo.resultado) return;
    escribirRespaldo(reg.id, (m) => {
      m[k] = nuevo;
    });
    const previo = pendientes.current.get(p.id);
    if (previo) clearTimeout(previo.t);
    if (guardarYa) {
      pendientes.current.delete(p.id);
      void persistir(p.id, nuevo);
    } else {
      const t = setTimeout(() => {
        pendientes.current.delete(p.id);
        void persistir(p.id, nuevo);
      }, 1000);
      pendientes.current.set(p.id, { t, r: nuevo });
    }
  }

  async function handleCerrar() {
    try {
      const r = await cerrar.mutateAsync(notaCierre.trim() || null);
      setConfirmarCierre(false);
      const nc = r.no_conformidades_creadas;
      toast.success(`Ficha cerrada: ${RESULTADO[r.resultado].label}`, {
        description:
          nc > 0
            ? `${nc} no conformidad${nc === 1 ? '' : 'es'} registrada${nc === 1 ? '' : 's'}`
            : undefined,
      });
    } catch (e) {
      toast.error('No se pudo cerrar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  async function reinspeccionar() {
    try {
      const r = await iniciar.mutateAsync({
        ficha_id: reg.ficha_id,
        obra_codigo: reg.obra_codigo,
        sub_partida_id: reg.sub_partida_id,
      });
      navigate(`/campo/calidad/${r.id}`);
    } catch (e) {
      toast.error('No se pudo iniciar la reinspección', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  const volverA = reg.obra_codigo ? `/campo/avance/${reg.obra_codigo}` : '/campo/avance';

  return (
    <div className="space-y-4 pb-24">
      <Link
        to={volverA}
        className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <CaretLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
        {reg.obra_codigo ?? 'Volver'}
      </Link>

      {/* Encabezado */}
      <Card className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-base font-semibold">{reg.ficha_nombre}</p>
            <p className="text-xs text-muted-foreground">
              {reg.obra_codigo && <span className="font-mono">{reg.obra_codigo}</span>}
              {reg.sub_partida_nombre && ` · ${reg.sub_partida_codigo} ${reg.sub_partida_nombre}`}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <Badge
              variant="outline"
              className={cn(
                reg.intento > 1 && reg.tipo === 'inspeccion' && 'border-amber-400 text-amber-800',
              )}
            >
              {etiquetaIntento(reg.tipo, reg.intento)}
            </Badge>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Inspector:{' '}
          <span className="font-medium text-foreground">
            {reg.iniciado_por_nombre ?? 'Sin usuario (modo desarrollo)'}
          </span>{' '}
          · {fechaHora(reg.iniciado_en)}
          {reg.cerrado_en && (
            <>
              <br />
              Cerrada por{' '}
              <span className="font-medium text-foreground">
                {reg.cerrado_por_nombre ?? 'Sin usuario'}
              </span>{' '}
              · {fechaHora(reg.cerrado_en)}
            </>
          )}
        </p>
        {reg.estado === 'borrador' ? (
          <div>
            <div className="mb-1 flex justify-between text-xs">
              <span className="text-muted-foreground">Respondidos</span>
              <span className="font-semibold tabular-nums">
                {respondidos}/{puntos.length}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200">
              <div
                className="h-full rounded-full bg-adelante-primary transition-all"
                style={{ width: `${puntos.length ? (respondidos / puntos.length) * 100 : 0}%` }}
              />
            </div>
          </div>
        ) : reg.estado === 'anulado' ? (
          <p className="rounded-md border border-zinc-300 bg-zinc-50 px-2 py-1 text-sm text-zinc-600">
            Registro anulado
          </p>
        ) : (
          reg.resultado && (
            <div className="space-y-1">
              <p
                className={cn(
                  'rounded-md border px-2 py-1 text-sm font-semibold',
                  RESULTADO[reg.resultado].clase,
                )}
              >
                {RESULTADO[reg.resultado].label}
              </p>
              {reg.liberado_en && (
                <p className="text-xs text-orange-800">
                  Liberado por {reg.liberado_por_nombre ?? 'admin'}: {reg.liberacion_justificacion}
                </p>
              )}
            </div>
          )
        )}
        {reg.nota && (
          <p className="whitespace-pre-line text-xs text-muted-foreground">{reg.nota}</p>
        )}
      </Card>

      {/* Secciones y puntos */}
      {reg.ficha.secciones.map((sec) => (
        <SeccionColapsable
          key={sec.id}
          nombre={sec.nombre}
          codigo={sec.sub_partida_revisada_codigo}
          total={sec.puntos.length}
          respondidos={sec.puntos.filter((p) => resp[p.id.toUpperCase()]?.resultado).length}
          fallas={
            sec.puntos.filter((p) => resp[p.id.toUpperCase()]?.resultado === 'no_cumple').length
          }
        >
          {sec.puntos.map((p) => (
            <PuntoTarjeta
              key={p.id}
              punto={p}
              resp={resp[p.id.toUpperCase()]}
              fotos={fotosPorPunto.get(p.id.toUpperCase()) ?? []}
              enCola={fotosEnCola.get(p.id.toUpperCase()) ?? 0}
              registroId={reg.id}
              editable={editable}
              onCambio={(c, ya) => actualizar(p, c, ya)}
              respondidoPor={
                respondidoPor.get(p.id.toUpperCase()) !== reg.iniciado_por_nombre
                  ? (respondidoPor.get(p.id.toUpperCase()) ?? null)
                  : null
              }
            />
          ))}
        </SeccionColapsable>
      ))}

      {/* Acciones */}
      <AccionesRegistro
        reg={reg}
        editable={editable}
        completos={respondidos === puntos.length}
        onCerrar={() => setConfirmarCierre(true)}
        onReinspeccionar={reinspeccionar}
        reinspeccionando={iniciar.isPending}
      />

      <Dialog
        open={confirmarCierre}
        onOpenChange={(o) => !cerrar.isPending && setConfirmarCierre(o)}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Cerrar ficha</DialogTitle>
            <DialogDescription>Al cerrar ya no se pueden cambiar las respuestas.</DialogDescription>
          </DialogHeader>
          <p
            className={cn(
              'rounded-md border px-2 py-1.5 text-sm font-semibold',
              RESULTADO[previsto].clase,
            )}
          >
            Resultado: {RESULTADO[previsto].label}
          </p>
          {previsto === 'rechazado' && reg.tipo === 'inspeccion' && (
            <p className="text-xs text-red-700">
              Hay puntos obligatorios que no cumplen: la sub-partida seguirá bloqueada hasta una
              reinspección aprobada.
            </p>
          )}
          <Textarea
            value={notaCierre}
            onChange={(e) => setNotaCierre(e.target.value)}
            placeholder="Observación general (opcional)"
            rows={3}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmarCierre(false)}
              disabled={cerrar.isPending}
            >
              Cancelar
            </Button>
            <Button onClick={handleCerrar} disabled={cerrar.isPending}>
              {cerrar.isPending ? 'Cerrando…' : 'Cerrar ficha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Punto de control
// ============================================================================

interface PuntoProps {
  punto: PuntoControl;
  resp: RespLocal | undefined;
  fotos: FotoCalidad[];
  /** Fotos tomadas en este teléfono que todavía se están subiendo. */
  enCola?: number;
  registroId: string;
  editable: boolean;
  onCambio: (cambio: Partial<RespLocal>, guardarYa: boolean) => void;
  /** Vista previa (admin): se puede tocar todo pero no se sube ninguna foto. */
  vistaPrevia?: boolean;
  /** Nombre de quien respondió, si no es el inspector que abrió la ficha. */
  respondidoPor?: string | null;
}

const BOTONES: Array<{ v: ResultadoPunto; label: string; activo: string }> = [
  {
    v: 'cumple',
    label: 'Cumple',
    activo: 'border-adelante-primary bg-adelante-primary text-adelante-foreground',
  },
  { v: 'no_cumple', label: 'No cumple', activo: 'border-red-500 bg-red-500 text-white' },
  { v: 'no_aplica', label: 'N/A', activo: 'border-zinc-500 bg-zinc-500 text-white' },
];

function PuntoTarjeta({
  punto: p,
  resp,
  fotos,
  enCola = 0,
  registroId,
  editable,
  onCambio,
  vistaPrevia = false,
  respondidoPor = null,
}: PuntoProps) {
  const r = resp ?? { resultado: null, valor: '', calificacion: null, nota: '' };
  const [verNota, setVerNota] = useState(r.nota !== '');
  const abrirCamara = useCamaraFotos();
  const eliminar = useEliminarFoto(registroId);
  const faltaFoto =
    p.requiere_foto &&
    r.resultado &&
    r.resultado !== 'no_aplica' &&
    fotos.length === 0 &&
    enCola === 0;
  const rango = fueraDeRango(p, r.valor);

  useEffect(() => {
    if (r.resultado === 'no_cumple') setVerNota(true);
  }, [r.resultado]);

  // Cámara en ráfaga: las fotos se encolan y suben en segundo plano.
  function tomarFotos() {
    if (vistaPrevia) {
      toast.info('Vista previa: aquí se abre la cámara en ráfaga');
      return;
    }
    abrirCamara({
      etiqueta: p.texto,
      vinculo: { registro_id: registroId, punto_id: p.id, momento: 'hallazgo' },
    });
  }

  return (
    <Card
      className={cn(
        'space-y-2 p-3',
        r.resultado === 'no_cumple' && 'border-red-300',
        !r.resultado && editable && 'border-dashed',
      )}
    >
      <div>
        <p className="text-sm font-medium">
          {p.texto}
          {p.obligatorio && (
            <span className="ml-1 text-red-600" title="Obligatorio">
              *
            </span>
          )}
        </p>
        {p.criterio && <p className="text-xs text-muted-foreground">{p.criterio}</p>}
        {respondidoPor && (
          <p className="text-[11px] text-muted-foreground">Respondió: {respondidoPor}</p>
        )}
        <p className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
          {p.obligatorio && <span className="font-medium text-red-700">Obligatorio</span>}
          {p.requiere_foto && <span>Requiere foto</span>}
          {p.tipo_valor === 'valor' && (p.valor_min !== null || p.valor_max !== null) && (
            <span>
              Rango: {p.valor_min ?? '—'} a {p.valor_max ?? '—'} {p.unidad ?? ''}
            </span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {BOTONES.map((b) => (
          <button
            key={b.v}
            type="button"
            disabled={!editable}
            onClick={() => onCambio({ resultado: b.v }, true)}
            className={cn(
              'rounded-md border px-2 py-2 text-xs font-semibold transition-colors disabled:cursor-default',
              r.resultado === b.v ? b.activo : 'border-border bg-white text-foreground',
              editable && r.resultado !== b.v && 'hover:bg-zinc-50',
            )}
          >
            {b.label}
          </button>
        ))}
      </div>

      {p.tipo_valor === 'valor' && (
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            value={r.valor}
            disabled={!editable}
            onChange={(e) => onCambio({ valor: e.target.value }, false)}
            onBlur={() => r.resultado && onCambio({}, true)}
            placeholder="Valor medido"
            className={cn(
              'h-8 w-32 rounded-md border px-2 text-sm tabular-nums disabled:opacity-70',
              rango ? 'border-red-400' : 'border-border',
            )}
          />
          <span className="text-xs text-muted-foreground">{p.unidad}</span>
          {rango && <span className="text-xs text-red-700">Fuera de rango</span>}
        </div>
      )}

      {p.tipo_valor === 'calificacion' && (
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              disabled={!editable}
              onClick={() => onCambio({ calificacion: n }, !!r.resultado)}
              className={cn(
                'h-8 w-8 rounded-md border text-xs font-semibold',
                r.calificacion === n
                  ? 'border-blue-500 bg-blue-500 text-white'
                  : 'border-border bg-white',
              )}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {verNota ? (
        <Textarea
          value={r.nota}
          disabled={!editable}
          onChange={(e) => onCambio({ nota: e.target.value }, false)}
          onBlur={() => r.resultado && onCambio({}, true)}
          placeholder={r.resultado === 'no_cumple' ? '¿Qué no cumple?' : 'Nota'}
          rows={2}
          className="text-sm"
        />
      ) : (
        editable && (
          <button
            type="button"
            onClick={() => setVerNota(true)}
            className="text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
          >
            + Nota
          </button>
        )
      )}

      {/* Fotos */}
      {(fotos.length > 0 || editable) && (
        <div className="flex flex-wrap items-center gap-2">
          {fotos.map((f) => (
            <div key={f.id} className="relative">
              <a href={f.url} target="_blank" rel="noreferrer">
                <img
                  src={f.thumb_url ?? f.url}
                  alt="Foto del punto"
                  className="h-16 w-16 rounded-md border border-border object-cover"
                  loading="lazy"
                />
              </a>
              {editable && (
                <button
                  type="button"
                  onClick={() => eliminar.mutate(f.id)}
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 shadow"
                  aria-label="Quitar foto"
                >
                  <Trash className="h-3.5 w-3.5 text-red-600" weight="bold" />
                </button>
              )}
            </div>
          ))}
          {enCola > 0 && (
            <div className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-md border border-border bg-zinc-50 text-[10px] font-medium text-muted-foreground">
              <CircleNotch className="h-5 w-5 animate-spin" weight="bold" aria-hidden="true" />
              {enCola} subiendo
            </div>
          )}
          {editable && (
            <button
              type="button"
              onClick={tomarFotos}
              className={cn(
                'flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed text-[10px] font-medium',
                faltaFoto ? 'border-red-400 text-red-700' : 'border-border text-muted-foreground',
              )}
            >
              <Camera className="h-5 w-5" weight="bold" aria-hidden="true" />
              Fotos
            </button>
          )}
          {faltaFoto && <span className="text-xs text-red-700">Falta la foto</span>}
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// Acciones (cerrar / reinspeccionar / liberar / anular)
// ============================================================================

function AccionesRegistro({
  reg,
  editable,
  completos,
  onCerrar,
  onReinspeccionar,
  reinspeccionando,
}: {
  reg: RegistroDetalle;
  editable: boolean;
  completos: boolean;
  onCerrar: () => void;
  onReinspeccionar: () => void;
  reinspeccionando: boolean;
}) {
  const [liberarAbierto, setLiberarAbierto] = useState(false);
  const [anularAbierto, setAnularAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const liberar = useLiberarRegistro(reg.id);
  const anular = useAnularRegistro(reg.id);

  const rechazadaSinLiberar =
    reg.tipo === 'inspeccion' &&
    reg.estado === 'cerrado' &&
    reg.resultado === 'rechazado' &&
    !reg.liberado_en;

  async function confirmar(accion: 'liberar' | 'anular') {
    try {
      if (accion === 'liberar') {
        await liberar.mutateAsync(texto.trim());
        toast.success('Inspección liberada');
        setLiberarAbierto(false);
      } else {
        await anular.mutateAsync(texto.trim());
        toast.success('Registro anulado');
        setAnularAbierto(false);
      }
      setTexto('');
    } catch (e) {
      toast.error('No se pudo completar', {
        description: e instanceof Error ? e.message : 'Error desconocido',
      });
    }
  }

  return (
    <>
      <div className="fixed inset-x-0 bottom-16 z-30 border-t border-border bg-white/95 px-4 py-2 backdrop-blur pb-[calc(0.5rem+env(safe-area-inset-bottom))] md:static md:border-0 md:bg-transparent md:p-0">
        <div className="mx-auto flex max-w-2xl flex-wrap justify-end gap-2">
          {esGestion() && reg.estado !== 'anulado' && (
            <Button variant="ghost" size="sm" onClick={() => setAnularAbierto(true)}>
              <Prohibit className="h-4 w-4" weight="bold" aria-hidden="true" />
              Anular
            </Button>
          )}
          {rechazadaSinLiberar && esAdmin() && (
            <Button variant="outline" size="sm" onClick={() => setLiberarAbierto(true)}>
              <SealCheck className="h-4 w-4" weight="bold" aria-hidden="true" />
              Liberar (excepción)
            </Button>
          )}
          {rechazadaSinLiberar && !esSoloLectura() && (
            <Button size="sm" onClick={onReinspeccionar} disabled={reinspeccionando}>
              <XCircle className="h-4 w-4" weight="bold" aria-hidden="true" />
              Reinspeccionar
            </Button>
          )}
          {editable && (
            <Button
              onClick={onCerrar}
              disabled={!completos}
              title={completos ? undefined : 'Faltan puntos por responder'}
            >
              <CheckCircle className="h-4 w-4" weight="bold" aria-hidden="true" />
              Cerrar ficha
            </Button>
          )}
        </div>
      </div>

      <Dialog
        open={liberarAbierto || anularAbierto}
        onOpenChange={(o) => {
          if (!o && !liberar.isPending && !anular.isPending) {
            setLiberarAbierto(false);
            setAnularAbierto(false);
            setTexto('');
          }
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>
              {liberarAbierto ? 'Liberación excepcional' : 'Anular registro'}
            </DialogTitle>
            <DialogDescription>
              {liberarAbierto
                ? 'Permite iniciar la sub-partida pese al rechazo. Los puntos obligatorios que no cumplieron quedan como no conformidades. Queda registrado quién y por qué.'
                : 'El registro deja de contar (y sus no conformidades abiertas se anulan). Use esto para registros hechos por error.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={liberarAbierto ? 'Justificación (mín. 10 caracteres)' : 'Motivo'}
            rows={3}
          />
          <DialogFooter>
            <Button
              onClick={() => confirmar(liberarAbierto ? 'liberar' : 'anular')}
              disabled={
                texto.trim().length < (liberarAbierto ? 10 : 5) ||
                liberar.isPending ||
                anular.isPending
              }
              variant={liberarAbierto ? 'default' : 'destructive'}
            >
              {liberarAbierto ? 'Liberar' : 'Anular'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
